import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Mail, MapPin, MessageCircle, Phone } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";

import { COMPANY } from "@/mock";
import { postContactLead } from "@/lib/api";
import { Container, makeMailUrl, makeTelUrl, makeWhatsAppUrl, setTitle } from "@/pages/_shared";

export default function ContactPage() {
  useEffect(() => setTitle("Contact"), []);

  const [form, setForm] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    product: "",
    quantity: "",
    message: "",
  });

  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.product) {
      toast.error("Please fill Name, Phone and Product requirement.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        company: form.company || undefined,
        phone: form.phone,
        email: form.email || undefined,
        product: form.product,
        quantity: form.quantity || undefined,
        message: form.message || undefined,
        source_page: "/contact",
      };

      const created = await postContactLead(payload);
      toast.success("Enquiry submitted", {
        description: `Saved successfully. Reference: ${created.id}`,
      });
      setForm({ name: "", company: "", phone: "", email: "", product: "", quantity: "", message: "" });
    } catch (err) {
      toast.error("Could not submit enquiry", {
        description: "Please try again or WhatsApp us. (Backend connection failed)",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Google Business / Maps embed
  // Use CID in *decimal* form (more reliable than hex in embed URLs)
  // Source listing CID: 0x3d2a5df2abb78aff  => 4407438482179853055
  // Embed query tightened with full address to match the exact Google Business listing pin
  const mapSrc =
    "https://www.google.com/maps?q=National%20Wire%20Products%2C%20IIT%20Road%2C%20Amingaon%2C%20Guwahati%20781001&output=embed&z=17";
  // Canonical Google listing URL (from your Google Business share link)
  const mapsListingUrl =
    "https://www.google.com/maps/place/National+Wire+Products+-+Gabion+Box,+Chainlink+Fence,+GI+Wire,+Barbed+Wire+Manufacturer/data=!4m2!3m1!1s0x0:0x3d2a5df2abb78aff?sa=X&ved=1t:2428&ictx=111";


  return (
    <div>
      <section className="py-14">
        <Container>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">Contact Us</h1>
          <p className="mt-4 text-base text-muted-foreground max-w-2xl">
            Get in touch for pricing, availability, bulk order supply, and project-specific requirements.
          </p>

          <div className="mt-10 grid gap-6 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-5">
              <div className="rounded-3xl border border-border/60 bg-background/70 p-6">
                <div className="text-sm font-extrabold tracking-tight">Contact</div>
                <div className="mt-3 grid gap-2 text-sm text-muted-foreground">
                  <div className="grid gap-1">
                    <div className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-4 w-4" />
                      <div className="grid">
                        <span>{COMPANY.addressLine}</span>
                      </div>
                    </div>

                    {COMPANY.addressLine2 ? (
                      <div className="flex items-start gap-2">
                        <MapPin className="mt-0.5 h-4 w-4" />
                        <div className="grid">
                          <span className="text-muted-foreground">{COMPANY.addressLine2}</span>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="grid gap-1">
                    {(COMPANY.phoneContacts || (COMPANY.phones || [COMPANY.phone]).map((p) => ({ label: "Phone", phone: p }))).map(
                      (c) => (
                        <a
                          key={`${c.label}-${c.phone}`}
                          className="flex items-center justify-between gap-3 rounded-xl border border-border/50 bg-background/60 px-3 py-2 hover:bg-secondary/40"
                          href={makeTelUrl(c.phone)}
                        >
                          <span className="inline-flex items-center gap-2">
                            <Phone className="h-4 w-4" />
                            <span className="font-semibold">{c.label}:</span>
                          </span>
                          <span className="font-semibold text-foreground">{c.phone}</span>
                        </a>
                      )
                    )}
                  </div>
                  <a
                    className="flex items-center gap-2 hover:text-foreground"
                    href={makeWhatsAppUrl(COMPANY.whatsapp, "Hello, I want to enquire about your products.")}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <MessageCircle className="h-4 w-4" /> {COMPANY.whatsapp}
                  </a>
                  <a className="flex items-center gap-2 hover:text-foreground" href={makeMailUrl(COMPANY.email)}>
                    <Mail className="h-4 w-4" /> {COMPANY.email}
                  </a>
                </div>

                <div className="mt-5 flex flex-col gap-2">
                  <Button className="bg-amber-500 text-slate-950 hover:bg-amber-400" asChild>
                    <Link to="/request-quote">
                      Request a Quote <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    className="border-emerald-200/60 bg-emerald-50 text-emerald-950 hover:bg-emerald-100"
                    onClick={() => window.open(makeWhatsAppUrl(COMPANY.whatsapp, "Hello, please share a quotation."), "_blank")}
                  >
                    <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp Us
                  </Button>
                </div>

                <Separator className="my-6" />

                <div className="text-xs text-muted-foreground">
                  Enquiries submitted here are saved to our system for follow-up. For urgent requirements, use WhatsApp for faster response.
                </div>
              </div>

              <div className="mt-6 overflow-hidden rounded-3xl border border-border/60 bg-background/70">
                <iframe
                  title="Google Map"
                  src={mapSrc}
                  className="h-72 w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <div className="flex items-center justify-between gap-3 border-t border-border/60 bg-secondary/20 px-4 py-3">
                  <div className="text-xs text-muted-foreground">
                    Verified location on Google Business
                  </div>
                  <a
                    href={mapsListingUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-foreground transition-colors hover:text-slate-900 hover:underline"
                  >
                    View on Google <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-3xl border border-border/60 bg-background/70 p-6">
                <div className="text-sm font-extrabold tracking-tight">Quick Enquiry</div>
                <p className="mt-2 text-sm text-muted-foreground">
                  Share product requirement + quantity. We’ll revert with pricing and lead time.
                </p>

                <form className="mt-6 grid gap-4" onSubmit={onSubmit}>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="text-xs font-semibold">Name *</label>
                      <Input
                        value={form.name}
                        onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold">Company</label>
                      <Input
                        value={form.company}
                        onChange={(e) => setForm((s) => ({ ...s, company: e.target.value }))}
                        placeholder="Company / Department"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold">Phone *</label>
                      <Input
                        value={form.phone}
                        onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
                        placeholder="Phone number"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold">Email</label>
                      <Input
                        value={form.email}
                        onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
                        placeholder="Email"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="text-xs font-semibold">Product requirement *</label>
                      <Input
                        value={form.product}
                        onChange={(e) => setForm((s) => ({ ...s, product: e.target.value }))}
                        placeholder="e.g., GI wire 2.5mm / Chainlink fence"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold">Quantity</label>
                      <Input
                        value={form.quantity}
                        onChange={(e) => setForm((s) => ({ ...s, quantity: e.target.value }))}
                        placeholder="e.g., 5 MT / 500 rolls"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-semibold">Message</label>
                    <Textarea
                      value={form.message}
                      onChange={(e) => setForm((s) => ({ ...s, message: e.target.value }))}
                      placeholder="Any standard, coating, packaging, delivery location…"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button className="bg-slate-900 text-slate-50 hover:bg-slate-800" type="submit" disabled={submitting}>
                      {submitting ? "Submitting…" : "Submit Enquiry"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        const text = `Hello, I want to enquire.\nProduct: ${form.product || "__"}\nQty: ${form.quantity || "__"}`;
                        window.open(makeWhatsAppUrl(COMPANY.whatsapp, text), "_blank");
                      }}
                    >
                      <MessageCircle className="mr-2 h-4 w-4" /> Send via WhatsApp
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
